/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any -- pcg-core surface is loose-typed by design */
/* eslint-disable @typescript-eslint/no-unsafe-argument -- pcg-core surface is loose-typed by design */

// JSX rewrite of the prototype's render function: the Murmur Triage research note
// (core-viewer.js renderTriage, 1152-1197) as JSX, plus the dev.18 coherent
// comparison and the handoff v0.8.3 marker-overlap research block. Controlled terminology is
// preserved verbatim: energy/triage output is a research signal, never a
// "murmur detected" finding — every sentence below is the prototype's copy.
//
// Deviation (same rule as the audit panel): when no
// serialized turbulent-energy model is supplied, the in-viewer cycle-energy
// research score is an ERROR STATE — the prototype would have quietly shown the
// logistic fallback's saturated score here.

import React, { ReactElement } from "react"
import { fmtNumber } from "../core/format"

// dev.18 renderTriage addition: the Coherent Energy research comparison block
// rendered above both triage branches. `energyBand` follows the viewer's inline
// sweep (the prototype re-rendered the triage note on every setEnergySweep);
// the default "s100" matches the prototype's bootstrap render, which ran before
// the recommended-band initialization.
const CoherentEnergyComparison = ({ core, energyBand }: { core: any; energyBand: string }): ReactElement => {
  const coherent = core.coherentEnergyV1(energyBand)
  return (
    <div style={{ marginBottom: 14, paddingBottom: 12, borderBottom: "1px solid #293548" }}>
      <strong>
        Coherent Energy v1.7 — Time-frequency envelope comparison · {(coherent.rangeHz || []).join("–")} Hz
      </strong>
      <br />
      Proposed SYS {coherent.proposedSys} · DIA {coherent.proposedDia} · peak recurrence{" "}
      {fmtNumber(coherent.sysRecurrencePct, 0)}% SYS / {fmtNumber(coherent.diaRecurrencePct, 0)}% DIA.
      <br />
      {coherent.reason}. Accepted Timing coverage {fmtNumber(coherent.acceptedTimingCoveragePct, 0)}%; research scoring
      coverage {fmtNumber(coherent.researchTimingCoveragePct, 0)}% using {coherent.scoredCycles} cycles.
    </div>
  )
}

// handoff v0.8.3 renderTriage addition: the marker-overlap recovery research
// block (`overlapComparison`), rendered ABOVE the coherent comparison in both
// triage branches. Same band-following rule as the coherent block. The
// <details> provenance JSON mirrors the prototype's audit-json <pre> verbatim.
const blockStyle = {
  marginBottom: 14,
  padding: 12,
  border: "1px solid #334155",
  borderRadius: 7,
  background: "#111827",
}
const MarkerOverlapComparison = ({ core, energyBand }: { core: any; energyBand: string }): ReactElement => {
  const overlap = core.markerOverlapResearch(energyBand)
  if (overlap.status !== "available") {
    return (
      <div style={blockStyle}>
        <strong>Marker-overlap recovery — Research only</strong>
        <br />
        Unavailable: {overlap.reason}. Provisional Results and export are unchanged.
      </div>
    )
  }
  return (
    <div style={blockStyle}>
      <strong>Marker-overlap recovery — Research only</strong>
      <br />
      Research classification: <strong>{overlap.researchClassification || "Unassessable"}</strong>
      <br />
      Activity existence: {overlap.activity} · phase: {overlap.phaseAssignment} · localization issue:{" "}
      {overlap.localizationIssue}.
      <br />
      {overlap.qualifyingMarkerWindows}/{overlap.scoredMarkerWindows} marker windows contained a distributed, repeatable
      sustained-energy candidate; {fmtNumber(overlap.recoveredDisplayPct, 0)}% of the comparison overlay lies inside
      provisional S1/S2 windows.
      <br />
      Reference: {(overlap.reference?.source || "unavailable").replace(/_/g, " ")} ·{" "}
      {overlap.reference?.referenceMarkerCount || 0} self-reference markers · contamination{" "}
      {overlap.reference?.possibleContamination ? "suspected" : "not detected"}.
      <br />
      Recurrence {fmtNumber(overlap.metrics?.recurrence?.recurrencePct, 0)}% · sustained occupancy{" "}
      {fmtNumber((overlap.metrics?.persistence?.medianSustainedOccupancy || 0) * 100, 0)}% · acoustic distinction{" "}
      {fmtNumber((overlap.metrics?.acousticDistinction?.score || 0) * 100, 0)}% · peak-position variability{" "}
      {fmtNumber((overlap.metrics?.timingOffset?.variabilityFractionOfLocalRr || 0) * 100, 1)}% of local RR.
      <br />
      <details style={{ marginTop: 7 }}>
        <summary style={{ cursor: "pointer", color: "#cbd5e1", fontWeight: 700 }}>
          Reference provenance and research metrics
        </summary>
        <pre className="audit-json" style={{ marginTop: 7, maxHeight: 260 }}>
          {JSON.stringify(
            { reference: overlap.reference, metrics: overlap.metrics, triggerReasons: overlap.triggerReasons },
            null,
            2
          )}
        </pre>
      </details>
      <span style={{ color: "#94a3b8" }}>{overlap.reason}</span>
      <br />
      <div style={{ marginTop: 7, color: "#94a3b8" }}>
        {overlap.reviewAvailable
          ? `Review available · ${overlap.triggerReasons.join(", ").replace(/_/g, " ")}. `
          : "No gated finding; manual inspection remains available. "}
        Grey = full acoustic envelope. Red appears only while this check is active. Provisional Results and export are
        unchanged.
      </div>
    </div>
  )
}

const PcgTriagePanel = ({ core, energyBand = "s100" }: { core: any; energyBand?: string }): ReactElement => {
  const D = core.ctx.payload
  const modelTriage = D.murmurTriage || D.examPackage?.murmur_triage
  if (modelTriage?.status === "available") {
    const confidenceMode = modelTriage.confidence_mode || {
      label: "Murmur Confidence",
      probability: modelTriage.probability,
      confidence: modelTriage.confidence,
      decision_threshold: modelTriage.decision_threshold,
      aggregation: modelTriage.aggregation || "mean",
      interpretation: "Conservative research signal.",
    }
    const reviewMode = modelTriage.review_priority_mode || null
    const renderMode = (mode: any): ReactElement => {
      const threshold = typeof mode.decision_threshold === "number" ? mode.decision_threshold : 0.29
      const label = mode.probability >= threshold ? "above research threshold" : "below research threshold"
      return (
        <>
          <strong>{mode.label}</strong>
          <br />
          Research probability {fmtNumber(mode.probability * 100, 0)}% · confidence{" "}
          {fmtNumber(mode.confidence * 100, 0)}%
          <br />
          {label}; threshold {fmtNumber(threshold * 100, 0)}%; aggregation {mode.aggregation}; fragments{" "}
          {modelTriage.n_fragments}.
          <br />
          {mode.interpretation}
        </>
      )
    }
    return (
      <div className="research-note">
        <MarkerOverlapComparison core={core} energyBand={energyBand} />
        <CoherentEnergyComparison core={core} energyBand={energyBand} />
        {renderMode(confidenceMode)}
        <br />
        <br />
        {reviewMode ? (
          <>
            {renderMode(reviewMode)}
            <br />
            <br />
          </>
        ) : null}
        Feature set {modelTriage.feature_set}; model {modelTriage.version}.
        <br />
        Murmur triage is a separate server-side Research model. Normal-viewer Energy remains a factual cycle-aligned
        guide layer.
      </div>
    )
  }
  const turbulentModelMissing = !core.ctx.turbulentModel
  const evidence = turbulentModelMissing ? null : core.structuredTurbulenceEvidence()
  const scoreText = turbulentModelMissing
    ? "Research score unavailable: turbulent-energy model not provided (fallback scoring disabled)"
    : evidence && typeof evidence.score === "number"
    ? `${fmtNumber(evidence.score * 100, 0)}% in-viewer cycle-energy research score`
    : "Unavailable for this recording"
  const detail = turbulentModelMissing
    ? "The serialized turbulent-energy model must ship with the guide payload; the logistic fallback is not displayed (it disagrees with the production forest on nearly every corpus recording)."
    : evidence
    ? `Energy: Sys ${fmtNumber(evidence.sysCyclePct, 0)}%, Dia ${fmtNumber(evidence.diaCyclePct, 0)}%, scored cycles ${
        evidence.cycles
      }.`
    : modelTriage?.reason
    ? `Texture murmur triage unavailable: ${modelTriage.reason}.`
    : "The recording did not pass the quality gates needed for research scoring."
  return (
    <div className="research-note">
      <MarkerOverlapComparison core={core} energyBand={energyBand} />
      <CoherentEnergyComparison core={core} energyBand={energyBand} />
      <strong>Murmur Triage</strong>
      <br />
      {scoreText}
      <br />
      {detail}
      <br />
      <br />
      Current normal-viewer Energy remains a factual cycle-aligned guide layer. Murmur triage is an opt-in research
      surface. Packaged texture triage artifact: murmur_triage_texture_v001; CirCor record-level CV F1 0.7666,
      sensitivity 0.7074, specificity 0.9711. The packaged texture model is versioned for research evaluation and is not
      yet executed in this browser view.
    </div>
  )
}

export default PcgTriagePanel
